<script setup lang="ts">
import { ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';
import { mdiCheckCircleOutline, mdiAlertCircleOutline, mdiAccountOutline, mdiEmailOutline, mdiDomain, mdiLockOutline, mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js';

const showPassword = ref(false);
const router = useRouter();
const authStore = useAuthStore();

const firstname = ref('');
const lastname = ref('');
const email = ref('');
const password = ref('');
const confirmpassword = ref('');
const company = ref('');
const checkbox = ref(false);
const regForm = ref<any>(null);
const loading = ref(false);
const errors = ref<Record<string, string[]>>({});
const successMessage = ref('');

const passwordRules = ref([
  (v: string) => !!v || 'Password is required',
  (v: string) => (v && v.length >= 8) || 'Password must be at least 8 characters',
  (v: string) => (v && v.length <= 20) || 'Password must be less than 20 characters'
]);

const confirmPasswordRules = ref([
  (v: string) => !!v || 'Confirm password is required',
  (v: string) => v === password.value || 'Passwords do not match'
]);

const emailRules = ref([
  (v: string) => !!v || 'E-mail is required',
  (v: string) => /.+@.+\..+/.test(v) || 'E-mail must be valid'
]);

const handleSubmit = async () => {
  try {
    loading.value = true;
    errors.value = {};
    successMessage.value = '';
    
    // Validate the form
    const isValid = await regForm.value?.validate();
    
    if (!isValid) {
      loading.value = false;
      return;
    }

    // Check if terms are agreed
    if (!checkbox.value) {
      errors.value = { general: ['Please agree to the terms and conditions'] };
      loading.value = false;
      return;
    }

   
    // Submit the form
    const success = await authStore.register({
      first_name: firstname.value,
      last_name: lastname.value,
      email: email.value,
      password: password.value,
      confirm_password: confirmpassword.value,
      company_name: company.value,
    });


    // If successful, show success message
    if (success) {
      successMessage.value = 'Registration successful! Please check your email for a verification link to activate your account.';
      // Reset form
      firstname.value = '';
      lastname.value = '';
      email.value = '';
      password.value = '';
      confirmpassword.value = '';
      company.value = '';
      checkbox.value = false;
    }
  } catch (err: any) {
    console.error("AN error ocured Response:", err);
    
    // Handle API response errors
    if (typeof err === 'string') {
      // If the error is a string message
      errors.value = { general: [err] };
    } else if (err?.errors) {
      // If there are field-specific errors
      errors.value = err.errors;
    } else {
      // Default error message
      errors.value = { general: ['Registration failed. Please try again.'] };
    }
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <v-form ref="regForm" @submit.prevent="handleSubmit" class="login-form">
    <v-alert 
      v-if="successMessage" 
      color="success" 
      variant="tonal" 
      density="compact" 
      :icon="mdiCheckCircleOutline"
      class="error-alert"
    >
      {{ successMessage }}
    </v-alert>
    
    <v-alert
      v-if="Object.keys(errors).length > 0"
      color="error"
      variant="tonal"
      density="compact"
      :icon="mdiAlertCircleOutline"
      class="error-alert"
    >
      <template v-if="errors.general">
        <div v-for="(message, index) in errors.general" :key="index">
          {{ message }}
        </div>
      </template>
      <template v-else>
        <div v-for="(messages, field) in errors" :key="field">
          <div v-for="(message, index) in messages" :key="index">
            {{ message }}
          </div>
        </div>
      </template>
    </v-alert>
    
    <v-row>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="firstname"
          density="comfortable"
          hide-details="auto"
          variant="outlined"
          color="primary"
          label="First name"
          class="input-field"
          :prepend-inner-icon="mdiAccountOutline"
          :rules="[(v: string) => !!v || 'First name is required']"
          :disabled="loading"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="lastname"
          density="comfortable"
          hide-details="auto"
          variant="outlined"
          color="primary"
          label="Last name"
          class="input-field"
          :prepend-inner-icon="mdiAccountOutline"
          :rules="[(v: string) => !!v || 'Last name is required']"
          :disabled="loading"
        />
      </v-col>
    </v-row>
    
    <v-text-field
      v-model="email"
      :rules="emailRules"
      label="Email"
      required
      density="comfortable"
      hide-details="auto"
      variant="outlined"
      color="primary"
      class="input-field"
      :prepend-inner-icon="mdiEmailOutline"
      :disabled="loading"
      autocomplete="email"
    />

    <v-text-field
      v-model="company"
      :rules="[(v: string) => !!v || 'Company name is required']"
      label="Company"
      required
      density="comfortable"
      hide-details="auto"
      variant="outlined"
      color="primary"
      class="input-field"
      :prepend-inner-icon="mdiDomain"
      :disabled="loading"
      autocomplete="organization"
    />
    
    <v-text-field
      v-model="password"
      :rules="passwordRules"
      label="Password"
      required
      density="comfortable"
      variant="outlined"
      color="primary"
      hide-details="auto"
      class="input-field"
      :prepend-inner-icon="mdiLockOutline"
      :append-inner-icon="showPassword ? mdiEyeOutline : mdiEyeOffOutline"
      :type="showPassword ? 'text' : 'password'"
      @click:append-inner="showPassword = !showPassword"
      :disabled="loading"
      autocomplete="new-password"
    />
        
    <v-text-field
      v-model="confirmpassword"
      :rules="confirmPasswordRules"
      label="Confirm Password"
      required
      density="comfortable"
      variant="outlined"
      color="primary"
      hide-details="auto"
      class="input-field"
      :prepend-inner-icon="mdiLockOutline"
      :append-inner-icon="showPassword ? mdiEyeOutline : mdiEyeOffOutline"
      :type="showPassword ? 'text' : 'password'"
      @click:append-inner="showPassword = !showPassword"
      :disabled="loading"
      autocomplete="new-password"
    />

    <div class="auth-options">
      <v-checkbox
        v-model="checkbox"
        label="I agree to the"
        required
        color="#243a70"
        density="compact"
        hide-details
        :disabled="loading"
      />
      <a href="#" class="forgot-link">Terms and Conditions</a>
    </div>
    
    <v-btn 
      block 
      class="sign-in-btn" 
      :loading="loading"
      :disabled="loading"
      @click="handleSubmit"
      type="submit"
    >
      Create Account
    </v-btn>
  </v-form>
</template>

<style lang="scss" scoped>
.login-form {
  .v-row {
    margin: 0 -8px;
    
    .v-col {
      padding: 0 8px;
    }
  }
}
</style>
